import { message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Card, Form, FormGroup, Modal, ModalBody, Spinner } from "reactstrap";
import { AllowAccess } from "../../../components/BlockAccess";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PaginationComponent,
  ProductCategoryTable,
  RSelect,
} from "../../../components/Component";
import { authHeader } from "../../../header";
import { useDebounce } from "../../../hooks/useDebounceSearch";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { statusOptions } from "../options_common";

export const ProductCategoryManagement = () => {
  const [modal, setModal] = useState({
    edit: false,
    add: false,
    view: false,
    delete: false,
  });
  const [formData, setFormData] = useState({
    name: "",
    status: "",
    description: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(7);
  const [productCategories, setProductCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadBtn, setIsLoadBtn] = useState(false);
  const [details, setDetails] = useState({});
  const [search, setSearch] = useState("");

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const debouncedSearchTerm = useDebounce(search, 500);

  useEffect(
    () => {
      // if (debouncedSearchTerm) {
      getAllProductCategories();
      setCurrentPage(1);
      // }v
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  const onInputChange = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (e, name) => {
    const { value } = e;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      status: "",
      description: "",
    });
  };

  const onFormCancel = () => {
    resetForm();
    setModal({ edit: false, add: false, delete: false });
  };

  const getAllProductCategories = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/trproduct-category?search=${search}`, {
        headers: authHeader(),
      });
      const { data } = await response.data;
      const resultData = data.body;
      if (resultData) {
        setProductCategories(resultData);
        setIsLoading(false);
      }
      return { formData };
    } catch (error) {
      const errMsg = error.response;

      message.error({
        content: errMsg.data.message,
        style: {
          marginTop: "20vh",
        },
      });
      setIsLoading(false);
    }
  };

  const createProductCategory = async (obj) => {
    try {
      setIsLoadBtn(true);
      setIsLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/auth/admin/trproduct-category`, obj, {
        headers: authHeader(),
      });
      const formData = await response.data;
      if (formData) {
        getAllProductCategories();
        setIsLoadBtn(false);
        setIsLoading(false);
        message.success({
          content: "Category Created Successfully",
          style: {
            marginTop: "20vh",
          },
        });
        resetForm();
      }
      return { formData };
    } catch (error) {
      const errMsg = error.response.data.message;
      setIsLoadBtn(false);
      setIsLoading(false);
      message.error({
        content: errMsg,
        style: {
          marginTop: "20vh",
        },
      });
      resetForm();
    }
  };

  const editProductCategory = async (obj) => {
    try {
      setIsLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/trproduct-category/${formData?.id}`,
        obj,
        {
          headers: authHeader(),
        }
      );
      const res = await response.data;
      if (res) {
        getAllProductCategories();
        setIsLoadBtn(false);
        setIsLoading(false);
        message.success({
          content: "Category Edited Successfully",
          style: {
            marginTop: "20vh",
          },
        });
        resetForm();
      }
    } catch (error) {
      const errMsg = error.response.data.message;
      setIsLoadBtn(false);
      setIsLoading(false);

      message.error({
        content: errMsg,
        style: {
          marginTop: "20vh",
        },
      });
      resetForm();
    }
  };

  const deleteProductCat = async () => {
    try {
      setIsLoading(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/auth/admin/trproduct-category/${formData.id}`,
        {
          headers: authHeader(),
        }
      );

      const data = await response.data;
      if (data) {
        getAllProductCategories();
        setIsLoadBtn(false);
        setIsLoading(false);
        setModal({ delete: false });
        resetForm();
      }
      return { data };
    } catch (error) {
      const errMsg = error.response;
      message.error({
        content: errMsg.data?.message,
        style: {
          marginTop: "20vh",
        },
      });
      setIsLoadBtn(false);
      setIsLoading(false);
      setModal({ delete: false });
      resetForm();
      return { message };
    }
  };

  const onFormSubmit = () => {
    createProductCategory(formData);
    setModal({ add: false });
  };

  const onEditSubmit = () => {
    editProductCategory(formData);
    setModal({ edit: false });
  };
  const onDeleteClick = (items) => {
    setModal({ delete: true });
    setFormData({
      id: items.id,
    });
  };

  const onEditClick = async (items) => {
    setModal({ edit: true });
    setFormData({
      name: items.name,
      status: items.status,
      description: items.description,
      id: items.id,
    });
  };

  const onViewClick = async (items) => {
    setModal({ view: true });
    setDetails(items);
  };

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = productCategories?.slice(indexOfFirstItem, indexOfLastItem);

  // Change Page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const { errors, register, handleSubmit } = useForm();

  useEffect(() => {
    getAllProductCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <Head title="Product Category Management" />
      <Content>
        <Block>
          <BlockHead>
            <BlockHeadContent>
              <div className="d-flex justify-content-between align-items-center">
                <BlockTitle tag="h4">Product Category</BlockTitle>
                <div className="d-flex align-items-center justify-center">
                  <div className="mr-2">
                    <input
                      className="form-control"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search Product Category..."
                    />
                  </div>
                  <AllowAccess allowedRights={["CREATE_PRODUCT_CATEGORY"]}>
                    <Button color="primary" onClick={() => setModal({ add: true })}>
                      <Icon name="plus" />
                      <span>Add Product Category</span>
                    </Button>
                  </AllowAccess>
                </div>
              </div>
            </BlockHeadContent>
          </BlockHead>
          <Card className="card-bordered card-preview">
            {isLoading ? (
              <Spinner
                color="primary"
                style={{
                  height: "3rem",
                  width: "3rem",
                  margin: "0 auto",
                }}
              />
            ) : (
              <ProductCategoryTable
                productCate={currentItems}
                modal={modal}
                onEditClick={onEditClick}
                handleView={onViewClick}
                handleDel={onDeleteClick}
              />
            )}
          </Card>
          <div className="card-inner">
            {productCategories && productCategories.length > 0 ? (
              <PaginationComponent
                itemPerPage={itemPerPage}
                totalItems={productCategories.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">No Product Category found</span>
              </div>
            )}
          </div>
        </Block>

        <Modal isOpen={modal.add} toggle={onFormCancel} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Add Product Category</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onFormSubmit)}>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Category Name</label>
                      <input
                        type="text"
                        name="name"
                        // value={formData.name}
                        placeholder="Category Name"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.name && <span className="invalid">{errors.name.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <RSelect
                        placeholder="Select Status"
                        value={statusOptions.find((d) => d.value === String(formData.status))}
                        options={statusOptions}
                        onChange={(e) => handleSelectChange(e, "status")}
                      />
                      {errors.status && <span className="invalid">{errors.status.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <FormGroup>
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        // value={formData.description}
                        placeholder="Your description"
                        onChange={(e) => onInputChange(e)}
                        className="form-control-xl form-control no-resize"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.description && <span className="invalid">{errors.description.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        {isLoadBtn ? (
                          <Button color="primary" disabled>
                            <Spinner size="sm" />
                            <span> Loading</span>
                          </Button>
                        ) : (
                          <Button color="primary" size="md" type="submit">
                            Submit
                          </Button>
                        )}
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modal.edit} toggle={onFormCancel} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Edit Product Category</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onEditSubmit)}>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Category Name</label>
                      <input
                        type="text"
                        name="name"
                        // value={formData.name}
                        value={formData.name}
                        placeholder="Category Name"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.name && <span className="invalid">{errors.name.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label">Status</label>
                      <RSelect
                        value={statusOptions.find((d) => d.value === String(formData.status))}
                        options={statusOptions}
                        onChange={(e) => setFormData({ ...formData, status: e.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <FormGroup>
                      <label className="form-label">Description</label>
                      <textarea
                        name="description"
                        // value={details.description}
                        value={formData.description}
                        placeholder="Your description"
                        onChange={(e) => onInputChange(e)}
                        className="form-control-xl form-control no-resize"
                        ref={register({
                          required: "This field is required",
                        })}
                      />
                      {errors.description && <span className="invalid">{errors.description.message}</span>}
                    </FormGroup>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        {isLoadBtn ? (
                          <Button color="primary" disabled>
                            <Spinner size="sm">Loading...</Spinner>
                            <span> Loading</span>
                          </Button>
                        ) : (
                          <Button color="primary" size="md" type="submit">
                            Submit
                          </Button>
                        )}
                      </li>
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light"
                        >
                          Cancel
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={modal.delete}
          toggle={() => setModal({ delete: false })}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Delete Category</h5>
              <div className="mt-4">
                <Button onClick={deleteProductCat} color="danger" size="lg">
                  Delete
                </Button>
                <Button onClick={onFormCancel} className="ml-2" color="secondary" size="lg">
                  Cancel
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modal.view} toggle={() => setModal({ view: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Product Category Details</h5>
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center my-2">
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Product Name: </span>

                      <span>{details?.name ?? "N/A"}</span>
                    </p>
                  </Col>
                  <Col md="6">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Status: </span>
                      <span
                        className={`ml-2 badge badge-dot badge-${details?.status === "ACTIVE" ? "success" : "danger"}`}
                      >
                        {details?.status}
                      </span>
                    </p>
                  </Col>
                </div>
                <div className="my-2">
                  <Col md="12">
                    <p className="p-0 m-0">
                      <span className="font-weight-bold">Description: </span>
                      <br />
                      <span>{details?.description}</span>
                    </p>
                  </Col>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};
